import { useEffect, useRef } from 'react';
import type { Rect, Size } from '../lib/cropMath';

// 1600 (was 1024) — meaningfully more pixel density on label-distance QRs,
// at a CPU cost we can absorb within the 200ms frame budget per the
// performance review on the Phase 2 amendment. Drop to 3 FPS via the
// frameIntervalMs override if a slower device blows the p95 target.
const DEFAULT_TARGET_LONG_EDGE_PX = 1600;
// 5 fps. Higher would just queue behind zxing-wasm's per-decode latency (the
// decodingRef guard in ContinuousScanPage drops overlapping decodes anyway).
const DEFAULT_FRAME_INTERVAL_MS = 200;
const JPEG_QUALITY = 0.85;

// Feature-detect the modern path once at module load. Some iOS webviews
// ship the OffscreenCanvas constructor without the convertToBlob method,
// so test the method directly. Falls back to HTMLCanvasElement.toBlob.
const HAS_OFFSCREEN_CONVERT_TO_BLOB = (() => {
  if (typeof OffscreenCanvas === 'undefined') return false;
  try {
    return typeof new OffscreenCanvas(1, 1).convertToBlob === 'function';
  } catch {
    return false;
  }
})();

export interface SampledFrame {
  capturedAt: string;
  /** rVFC's monotonic media-time stamp, useful for diagnostics. */
  mediaTimestampMs: number;
  /** ImageData fed to the QR decoder (downscaled to `decodeFrameSize`). */
  imageData: ImageData;
  /** Dimensions of `imageData`. Bboxes from the decoder are in this coord space. */
  decodeFrameSize: Size;
  /** Native-resolution ImageData snapshot — the source for per-QR crop encodes. */
  originalImageData: ImageData;
  /** Dimensions of `originalImageData`. */
  originalFrameSize: Size;
  /**
   * Encode a sub-rect of `originalImageData` as JPEG. Called once per fresh
   * QR after a decode hit. Each crop is encoded from the same ImageData
   * snapshot — temporally consistent across all crops in a multi-QR frame.
   */
  encodeJpegCrop: (rect: Rect) => Promise<Blob>;
  /**
   * Encode the DOWNSCALED `imageData` buffer (target long edge = the
   * sampler's `targetLongEdgePx`) as JPEG. Cheaper than a native-resolution
   * `encodeJpegCrop(fullFrame)` — used by Cycle Count for the ~15 fps
   * WebSocket upload path (see todo 180).
   */
  encodeDecodedJpeg: () => Promise<Blob>;
}

export interface UseFrameSamplerArgs {
  videoRef: React.MutableRefObject<HTMLVideoElement | null>;
  /** When false, the rVFC callback no-ops; pausing does NOT cancel in-flight uploads. */
  enabled: boolean;
  /**
   * Flips true once the `<video>` element is in the DOM and the camera
   * stream is attached. The effect deps include this so the rVFC loop
   * actually starts when the element appears — `videoRef.current` going
   * from null → non-null does NOT trigger a re-render on its own.
   */
  videoReady: boolean;
  onFrame: (frame: SampledFrame) => void;
  targetLongEdgePx?: number;
  frameIntervalMs?: number;
}

/**
 * Mounts a `requestVideoFrameCallback` loop. The loop runs once-per-mount;
 * pause/resume flips a ref, not the loop's mount. Every value the rVFC
 * callback touches lives in a ref — `useState` would close over the
 * initial render's value, miss subsequent toggles, and silently keep
 * sampling after the user paused.
 *
 * `document.visibilitychange === 'hidden'` automatically pauses sampling
 * via the enabled ref — see ContinuousScanPage.
 */
export function useFrameSampler(args: UseFrameSamplerArgs): void {
  const { videoRef, enabled, videoReady, onFrame, targetLongEdgePx, frameIntervalMs } = args;

  // All sampling-loop state lives in refs to avoid stale-closure bugs in
  // the long-lived rVFC callback.
  const enabledRef = useRef(enabled);
  const onFrameRef = useRef(onFrame);
  const lastEmitRef = useRef(0);
  const handleRef = useRef<number | null>(null);
  const decodeCanvasRef = useRef<OffscreenCanvas | null>(null);
  const decodeCtxRef = useRef<OffscreenCanvasRenderingContext2D | null>(null);
  const originalCanvasRef = useRef<OffscreenCanvas | null>(null);
  const originalCtxRef = useRef<OffscreenCanvasRenderingContext2D | null>(null);

  // Keep latest values reachable from the long-lived loop. Writes happen
  // in an effect (not during render) so refs stay strict-mode safe.
  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);
  useEffect(() => {
    onFrameRef.current = onFrame;
  }, [onFrame]);

  const longEdge = targetLongEdgePx ?? DEFAULT_TARGET_LONG_EDGE_PX;
  const interval = frameIntervalMs ?? DEFAULT_FRAME_INTERVAL_MS;

  useEffect(() => {
    if (!videoReady) {
      // eslint-disable-next-line no-console
      console.debug('[frame-sampler] skip mount: videoReady=false');
      return;
    }
    const video = videoRef.current;
    if (!video) {
      // eslint-disable-next-line no-console
      console.warn('[frame-sampler] skip mount: videoRef.current is null');
      return;
    }
    if (typeof video.requestVideoFrameCallback !== 'function') {
      // eslint-disable-next-line no-console
      console.error(
        '[frame-sampler] video.requestVideoFrameCallback is not a function — ' +
        'Safari < 15.4 or an experimental-flag build. No frames will be sampled.'
      );
      return;
    }
    // eslint-disable-next-line no-console
    console.debug('[frame-sampler] mounted; scheduling first rVFC tick');

    // Camera-switch guard. On track replacement (rear ↔ front) the
    // video element fires loadedmetadata asynchronously relative to
    // srcObject reassignment; for 1-2 rVFC ticks vid.videoWidth can
    // briefly report stale dimensions while drawImage already pulls
    // new-track pixels. Result without this gate: the first one or two
    // uploads after a camera flip carry a stretched/misaligned crop.
    // The flag is reset to false after the next emit, dropping that
    // one tick.
    let pendingResync = false;
    const onMetadataChange = () => {
      pendingResync = true;
    };
    video.addEventListener('loadedmetadata', onMetadataChange);

    let lastDiagLog = 0;
    const callback: VideoFrameRequestCallback = (mediaTimeMs) => {
      // Rate-limited diagnostic so we can see the loop is actually ticking
      // even when the enabled/interval gates are silencing it.
      const nowDiag = performance.now();
      if (nowDiag - lastDiagLog > 1000) {
        lastDiagLog = nowDiag;
        // eslint-disable-next-line no-console
        console.debug('[frame-sampler] tick', {
          enabled: enabledRef.current,
          pendingResync,
          sinceLastEmitMs: Math.round(nowDiag - lastEmitRef.current),
        });
      }

      if (!enabledRef.current) {
        // Sampling paused — re-schedule but skip the work. We don't
        // cancel; the loop must resume cleanly on `enabled` re-flip.
        scheduleNext();
        return;
      }

      if (pendingResync) {
        pendingResync = false;
        scheduleNext();
        return;
      }

      const now = performance.now();
      if (now - lastEmitRef.current < interval) {
        scheduleNext();
        return;
      }
      lastEmitRef.current = now;

      try {
        emitFrame(video, mediaTimeMs).catch(() => {
          // emitFrame swallows its own errors; this catch is defensive.
        });
      } catch {
        /* drop frame, keep loop alive */
      }
      scheduleNext();
    };

    const emitFrame = async (vid: HTMLVideoElement, mediaTimeMs: number) => {
      const srcW = vid.videoWidth;
      const srcH = vid.videoHeight;
      if (srcW === 0 || srcH === 0) return;

      // Downscale for the decoder.
      const max = Math.max(srcW, srcH);
      const scale = max > longEdge ? longEdge / max : 1;
      const targetW = Math.max(1, Math.round(srcW * scale));
      const targetH = Math.max(1, Math.round(srcH * scale));

      let decodeCanvas = decodeCanvasRef.current;
      if (!decodeCanvas || decodeCanvas.width !== targetW || decodeCanvas.height !== targetH) {
        decodeCanvas = new OffscreenCanvas(targetW, targetH);
        decodeCanvasRef.current = decodeCanvas;
        decodeCtxRef.current = decodeCanvas.getContext('2d');
      }
      const decodeCtx = decodeCtxRef.current;
      if (!decodeCtx) return;

      decodeCtx.drawImage(vid, 0, 0, targetW, targetH);
      const imageData = decodeCtx.getImageData(0, 0, targetW, targetH);

      // Native-resolution snapshot for per-QR crop encodes. Captured every
      // tick whether or not a fresh QR will fire — the alternative (lazy
      // capture inside encodeJpegCrop) would race the video element since
      // the next rVFC tick will already have advanced the frame. The
      // snapshot is released when SampledFrame goes out of scope at the
      // end of the consumer's tick.
      let originalCanvas = originalCanvasRef.current;
      if (!originalCanvas || originalCanvas.width !== srcW || originalCanvas.height !== srcH) {
        originalCanvas = new OffscreenCanvas(srcW, srcH);
        originalCanvasRef.current = originalCanvas;
        originalCtxRef.current = originalCanvas.getContext('2d');
      }
      const originalCtx = originalCtxRef.current;
      if (!originalCtx) return;
      originalCtx.drawImage(vid, 0, 0, srcW, srcH);
      const originalImageData = originalCtx.getImageData(0, 0, srcW, srcH);

      const encodeJpegCrop = (rect: Rect): Promise<Blob> => {
        return encodeCropFromImageData(originalImageData, rect);
      };
      const encodeDecodedJpeg = (): Promise<Blob> =>
        encodeCropFromImageData(imageData, { x: 0, y: 0, w: targetW, h: targetH });

      onFrameRef.current({
        capturedAt: new Date().toISOString(),
        mediaTimestampMs: mediaTimeMs,
        imageData,
        decodeFrameSize: { width: targetW, height: targetH },
        originalImageData,
        originalFrameSize: { width: srcW, height: srcH },
        encodeJpegCrop,
        encodeDecodedJpeg,
      });
    };

    const scheduleNext = () => {
      handleRef.current = video.requestVideoFrameCallback(callback);
    };

    scheduleNext();

    return () => {
      video.removeEventListener('loadedmetadata', onMetadataChange);
      if (handleRef.current !== null) {
        video.cancelVideoFrameCallback(handleRef.current);
        handleRef.current = null;
      }
    };
    // Intentionally NOT depending on `enabled` — the loop reads via ref
    // so it can toggle pause/resume without remounting. videoRef is a
    // ref-object identity, stable across renders. `videoReady` IS in deps
    // so the loop actually mounts once the <video> element + camera
    // stream are attached (the parent flips this true after start()).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [longEdge, interval, videoReady]);
}

/**
 * Encode a sub-rect of an ImageData as JPEG. Uses createImageBitmap +
 * OffscreenCanvas.convertToBlob where available; falls back to a detached
 * HTMLCanvasElement + toBlob for older iOS Safari / embedded webviews.
 * Always closes the intermediate ImageBitmap — they are not GC-managed
 * on a useful timescale.
 */
async function encodeCropFromImageData(source: ImageData, rect: Rect): Promise<Blob> {
  const w = Math.max(1, rect.w);
  const h = Math.max(1, rect.h);

  // Modern path: createImageBitmap can take a sub-rect directly.
  if (typeof createImageBitmap === 'function' && HAS_OFFSCREEN_CONVERT_TO_BLOB) {
    const bitmap = await createImageBitmap(source, rect.x, rect.y, w, h);
    try {
      const out = new OffscreenCanvas(w, h);
      const outCtx = out.getContext('2d');
      if (!outCtx) throw new Error('OffscreenCanvas 2d context unavailable');
      outCtx.drawImage(bitmap, 0, 0);
      return await out.convertToBlob({ type: 'image/jpeg', quality: JPEG_QUALITY });
    } finally {
      // ImageBitmaps are not GC-managed on a useful timescale — explicit
      // close prevents heap inflation on a long sweep.
      bitmap.close();
    }
  }

  // Fallback for browsers shipping OffscreenCanvas without convertToBlob
  // (some iOS Safari + embedded-webview environments). Paint the
  // source ImageData straight into the crop canvas via the dirty-rect
  // form of putImageData — source-rect coords land at destination (0,0)
  // when dx/dy are negated. Single allocation, single copy.
  const cropCanvas = document.createElement('canvas');
  cropCanvas.width = w;
  cropCanvas.height = h;
  const cropCtx = cropCanvas.getContext('2d');
  if (!cropCtx) throw new Error('canvas 2d context unavailable');
  cropCtx.putImageData(source, -rect.x, -rect.y, rect.x, rect.y, w, h);

  return new Promise<Blob>((resolve, reject) => {
    cropCanvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('toBlob returned null'))),
      'image/jpeg',
      JPEG_QUALITY
    );
  });
}
